// BroncoSDK.js
class BroncoSDK {
    constructor(config = {}) {
        this.baseURL = config.baseURL || '';
        this.session = null;
        this.userUUID = null;
    }

    // Set auth details
    setAuth(session, userUUID) {
        this.session = session;
        this.userUUID = userUUID;
    }

    // Clear auth details
    clearAuth() {
        this.session = null;
        this.userUUID = null;
    }

    // Get common headers
    getHeaders(includeContentType = false) {
        const headers = {
            'X-Auth-Session': this.session,
            'X-User-UUID': this.userUUID,
        };

        if (includeContentType) {
            headers['Content-Type'] = 'application/json';
        }

        return headers;
    }

    // Generic request method
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const headers = this.getHeaders(!!options.body);

        console.log('API Request', url, options);

        try {
            const response = await fetch(url, {
                headers,
                ...options,
                body: options.body ? JSON.stringify(options.body) : undefined,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('API Request failed:', error);
            throw error;
        }
    }

    async getAnalyticsProofOfPlay() {
        return this.request('/brood/pop/analytics', {
            method: 'GET'
        });
    }

    async getUserPermissions() {
        return this.request(`/user/${this.userUUID}/permissions`, {
            method: 'GET'
        });
    }

    // Role Management API
    async getRoles() {
        return this.request('/api/roles', { method: 'GET' });
    }

    async getRole(roleUUID) {
        return this.request(`/api/roles/${roleUUID}`, { method: 'GET' });
    }

    async createRole(data) {
        return this.request('/api/roles', { method: 'POST', body: data });
    }

    async updateRole(roleUUID, data) {
        return this.request(`/api/roles/${roleUUID}`, { method: 'PUT', body: data });
    }

    async deleteRole(roleUUID) {
        return this.request(`/api/roles/${roleUUID}`, { method: 'DELETE' });
    }

    async getAvailablePermissions() {
        return this.request('/api/permissions', { method: 'GET' });
    }

    // User Profile
    async changePassword(currentPassword, newPassword) {
        return this.request('/api/user/change-password', {
            method: 'POST',
            body: { current_password: currentPassword, new_password: newPassword },
        });
    }

    // API Tokens
    async getTokens() {
        return this.request('/api/tokens', { method: 'GET' });
    }

    async createToken(data) {
        return this.request('/api/tokens', { method: 'POST', body: data });
    }

    async revokeToken(tokenUUID) {
        return this.request(`/api/tokens/${tokenUUID}`, { method: 'DELETE' });
    }

    // NOTE: updateUserScope removed — scope is determined by the assigned role's
    // scope:* permission, not set per-user. Assign a different role to change scope.

    // Move a system to a different system_group
    async moveSystem(systemUUID, sourceSystemGroupUUID, targetSystemGroupUUID) {
        return this.request('/system/move', {
            method: 'POST',
            body: {
                systemUUID,
                sourceSystemGroupUUID,
                targetSystemGroupUUID,
            },
        });
    }

    // Move a system_group to a different parent (or to root if targetParentUUID is null)
    async moveSystemGroup(systemGroupUUID, targetParentUUID, accountUUID) {
        return this.request('/system_group/move', {
            method: 'POST',
            body: {
                systemGroupUUID,
                targetParentUUID: targetParentUUID || null,
                accountUUID,
            },
        });
    }
}

export default BroncoSDK;